// Step-by-step tutorials for ConstructHelp features
export const ROLES = [
  { key: 'homeowner', label: 'Homeowners', color: 'bg-[#2B4C6F]' },
  { key: 'contractor', label: 'Contractors', color: 'bg-[#D95800]' },
  { key: 'worker', label: 'Workers', color: 'bg-emerald-700' },
  { key: 'everyone', label: 'Everyone', color: 'bg-neutral-700' },
];

export const TUTORIALS = [
  {
    slug: 'post-a-job',
    role: 'homeowner',
    title: 'How to Post a Job and Get Quotes',
    summary: 'Describe your project once and let vetted contractors come to you with quotes.',
    minutes: 3,
    steps: [
      { title: 'Create your free account', body: 'Click "Get Started" and sign up as a Homeowner. It takes under a minute and posting jobs is always free.' },
      { title: 'Click "Post a Job" from your dashboard', body: 'Give your project a clear title (e.g. "Replace 20 sq ft of drywall in garage") — specific titles attract better-matched pros.' },
      { title: 'Describe the work and add photos', body: 'Explain what needs doing, your timeline, and attach photos of the area. Photos help contractors quote accurately without a site visit.' },
      { title: 'Set your budget and location', body: 'Enter a budget range (or mark it flexible) and your city/state so nearby pros see your job first.' },
      { title: 'Review applications', body: 'Contractors and workers apply with their price and profile. Compare their reviews, past work, and verification badges before choosing.' },
      { title: 'Message before you hire', body: 'Use the built-in chat to ask questions, confirm scope, and agree on details — everything stays documented in one place.' },
    ],
    tips: [
      'Jobs with photos get significantly more applications.',
      'Never pay outside the platform — escrow protects you if something goes wrong.',
    ],
  },
  {
    slug: 'hire-and-pay-with-escrow',
    role: 'homeowner',
    title: 'Hiring Safely with Escrow Payments',
    summary: 'Fund the job upfront, release payment only when the work is done to your satisfaction.',
    minutes: 4,
    steps: [
      { title: 'Accept an application', body: 'From your job page, review applicants and accept the pro you want. This creates the working agreement at the agreed price.' },
      { title: 'Fund the escrow', body: 'Pay the agreed amount by card (processed securely by Stripe). The money is held safely in escrow — the pro can see it\'s funded but can\'t touch it yet.' },
      { title: 'Work gets done', body: 'The pro completes the job knowing payment is guaranteed. You can chat, share progress photos, and track everything from the job page.' },
      { title: 'Release the payment', body: 'Happy with the result? Hit "Release Payment" and the funds transfer to the pro. Only you can trigger the release.' },
      { title: 'Leave a review', body: 'Rate the pro and describe your experience — reviews keep quality high for everyone on the platform.' },
    ],
    tips: [
      'If there\'s a dispute, our team can step in while funds are still protected in escrow.',
      'Escrow beats deposits: the pro is committed, and your money is never at risk.',
    ],
  },
  {
    slug: 'find-work-and-apply',
    role: 'worker',
    title: 'Finding Work and Applying to Jobs',
    summary: 'Build a strong profile, browse open jobs near you, and win your first hire.',
    minutes: 3,
    steps: [
      { title: 'Complete your profile', body: 'Add your trades/skills, years of experience, service area, and photos of past work. Complete profiles appear higher in searches and win more jobs.' },
      { title: 'Browse jobs', body: 'Open "Browse Jobs" and filter by trade, location, and budget. New jobs are posted daily — check often or enable notifications.' },
      { title: 'Apply with a personal message', body: 'State your price and write 2-3 sentences on why you\'re the right fit. Mention similar jobs you\'ve done — generic applications get skipped.' },
      { title: 'Chat with the poster', body: 'If they\'re interested, they\'ll message you. Respond quickly — fast replies dramatically increase your hire rate.' },
      { title: 'Get hired and paid via escrow', body: 'Once accepted, the client funds escrow before you start. Finish the work, they release the funds, and you get paid — guaranteed.' },
    ],
    tips: [
      'Free plan includes monthly applications; Pro unlocks unlimited applies plus profile boosts.',
      'Ask happy clients for reviews — they compound into more work over time.',
    ],
  },
  {
    slug: 'messaging-and-notifications',
    role: 'everyone',
    title: 'Messaging & Notifications',
    summary: 'Keep every conversation, quote and decision documented in one place.',
    minutes: 2,
    steps: [
      { title: 'Start a conversation', body: 'Message anyone from a job application, a marketplace listing ("Contact Seller"), or a pro\'s public profile.' },
      { title: 'Use the Messages inbox', body: 'All conversations live under Messages in your dashboard, with unread counts so nothing slips through.' },
      { title: 'Share photos and files', body: 'Attach photos of the site, sketches, or documents directly in chat to align on scope fast.' },
      { title: 'Watch the bell', body: 'The notification bell alerts you to new applications, offers, payments, weather warnings on job days, and messages — with links straight to the action.' },
    ],
    tips: [
      'Keeping negotiations in-platform protects you: chat history counts if a dispute ever arises.',
    ],
  },
  {
    slug: 'buy-on-the-marketplace',
    role: 'everyone',
    title: 'Buying Tools & Materials (and Making Offers)',
    summary: 'Shop new and used gear from local pros — and negotiate the price in one tap.',
    minutes: 3,
    steps: [
      { title: 'Browse the Marketplace', body: 'Open Marketplace from the top menu. Filter by category (power tools, materials, heavy equipment...), condition, price and location.' },
      { title: 'Check the listing', body: 'Review photos, condition, seller info and delivery options (pickup, delivery, or shipping).' },
      { title: 'Buy instantly — or make an offer', body: 'Hit "Buy Now" to check out at the asking price via Stripe. Think it\'s worth less? Tap "Make an Offer" and send your price.' },
      { title: 'Negotiate', body: 'The seller can accept, decline, or counter your offer. If they counter, you\'ll get a notification and email — accept the counter to lock that price.' },
      { title: 'Check out at your negotiated price', body: 'Once an offer is accepted, the Buy button switches to your agreed price. Pay securely and arrange handover with the seller in chat.' },
    ],
    tips: [
      'Save a search with filters to get alerts when matching items are listed.',
      'Offers well below asking (under ~50%) are usually declined — start reasonable.',
    ],
  },
  {
    slug: 'sell-on-the-marketplace',
    role: 'everyone',
    title: 'Selling Tools & Materials with AI Listings',
    summary: 'Snap a photo, let AI write the listing, and handle offers from your dashboard.',
    minutes: 3,
    steps: [
      { title: 'Click "Sell an Item"', body: 'From the Marketplace, hit Sell. Upload up to 5 photos of your tool or leftover materials.' },
      { title: 'Let AI fill in the details', body: 'After uploading a photo, use the AI auto-fill: it recognizes the item and drafts the title, description, category and a fair price. Edit anything before publishing.' },
      { title: 'Set price, condition and delivery', body: 'Choose condition (new → for-parts), mark the price negotiable if you\'ll take offers, and pick pickup/delivery/shipping.' },
      { title: 'Respond to offers', body: 'Buyers can send price offers. In "My Listings" you\'ll see each offer with Accept / Counter / Decline buttons — counter with your minimum and the buyer can accept in one tap.' },
      { title: 'Get paid', body: 'Checkout runs through Stripe. You\'ll be notified when an item sells; arrange handover with the buyer in chat.' },
    ],
    tips: [
      'Listings with clear, well-lit photos sell fastest.',
      'A small platform commission is deducted on sale — no fees for listing.',
    ],
  },
  {
    slug: 'invoices-and-estimates',
    role: 'contractor',
    title: 'Creating Invoices & Estimates',
    summary: 'Send professional, branded PDFs to clients in minutes.',
    minutes: 3,
    steps: [
      { title: 'Open Invoices from your dashboard', body: 'Choose whether you\'re creating an Estimate (a quote before work) or an Invoice (a bill after work).' },
      { title: 'Add line items', body: 'List labor, materials and quantities. Totals, and tax if you add it, are calculated automatically.' },
      { title: 'Add your logo', body: 'Upload your company logo once in settings — it appears on every PDF you generate, making your paperwork look professional.' },
      { title: 'Download or send', body: 'Export a clean PDF to email or print, or send it to the client through the platform.' },
      { title: 'Convert estimates to invoices', body: 'When a quote is approved, convert it to an invoice in one click — no retyping.' },
    ],
    tips: [
      'Detailed line items reduce pushback — clients trust itemized pricing.',
    ],
  },
  {
    slug: 'ai-lead-finder',
    role: 'contractor',
    title: 'Using the AI Lead Finder',
    summary: 'Let the AI hunt homeowner leads in your area and draft the outreach for you.',
    minutes: 4,
    steps: [
      { title: 'Open the Lead Finder', body: 'From your contractor dashboard, open the AI Lead Finder panel. Set your trade and target area.' },
      { title: 'Run a hunt', body: 'The agent searches the web for homeowners actively looking for your kind of work and returns a scored list of prospects.' },
      { title: 'Review lead details', body: 'Each lead shows the source, what the homeowner needs, and contact information where available.' },
      { title: 'Send AI-drafted outreach', body: 'The agent drafts a personalized message (email or SMS) for each lead — review, tweak and send without leaving the dashboard.' },
      { title: 'Track responses', body: 'Replies flow back into your outreach inbox so you can follow up and convert leads into booked jobs.' },
    ],
    tips: [
      'Leads use credits — check your balance in the panel before a big hunt.',
      'Personalized first lines get far more replies than generic pitches.',
    ],
  },
  {
    slug: 'membership-plans',
    role: 'everyone',
    title: 'Understanding Membership Plans',
    summary: 'What\'s free, what\'s Pro, and when upgrading pays for itself.',
    minutes: 2,
    steps: [
      { title: 'Workers: Free vs Pro', body: 'Free covers a profile and a monthly allowance of job applications. Pro unlocks unlimited applications and boosted visibility in search results.' },
      { title: 'Contractors: Starter vs Business', body: 'Starter covers the essentials to win work. Business adds pro tools: featured job posts, advanced analytics, and the full Pro toolkit.' },
      { title: 'Homeowners: always free', body: 'Posting jobs, hiring, messaging and escrow protection cost homeowners nothing.' },
      { title: 'Upgrade or cancel anytime', body: 'Manage your plan from the Pricing page or account settings. Billing is handled by Stripe; cancel anytime and keep access until the period ends.' },
    ],
    tips: [
      'One extra job won usually pays for months of Pro — upgrade when applications become your bottleneck.',
    ],
  },
];

export const getTutorial = (slug) => TUTORIALS.find((t) => t.slug === slug);
